import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Flex, Modal, ModalProps, Typography } from 'antd';

import { useBrandsSlice } from 'entities/Brand/hooks/useBrandsSlice';
import { useMedia } from 'shared/hooks';
import { EMainRoutes, EYmEvents, EYmEventsO1, EYmEventsO2 } from 'shared/typings';
import { ButtonStyleLink } from 'shared/ui';
import { sendYmEvent } from 'shared/utils';

const { Title, Text } = Typography;

interface IJoinBrandModalProps extends ModalProps {}

export const JoinBrandModal: FC<IJoinBrandModalProps> = ({ ...props }) => {
	const { t } = useTranslation();
	const isMd = useMedia();
	const { openedModal, setOpenedModal } = useBrandsSlice();

	if (!openedModal) return null;

	const modalMap: Record<typeof openedModal, { title: ReactNode; text: ReactNode; buttons: ReactNode }> = {
		no_source: {
			title: t('Пока вы не можете подать заявку на офферы'),
			text: (
				<>
					<Text type="secondary" style={{ fontSize: 16 }}>
						{t(
							'Чтобы подать заявку на офферы, вы должны создать площадку и дождаться пока завершится ее проверка командой Affilead.',
						)}
					</Text>
					<Text type="secondary" style={{ fontSize: 16 }}>
						{t(
							'Вы можете создать площадку в любое время в разделе “Каталог” либо “Мои площадки”, просто нажав на кнопку "Добавить площадку"',
						)}
					</Text>
				</>
			),
			buttons: (
				<Flex justify="end" vertical={isMd} gap={16}>
					<Button type="text" onClick={() => setOpenedModal(null)}>
						{t('Создать позже')}
					</Button>
					<ButtonStyleLink
						type="primary"
						to={`/${EMainRoutes.SOURCES}/${EMainRoutes.CREATE}`}
						onClick={() => {
							sendYmEvent({
								event: EYmEvents.CLICK_ADD_ADSPACE,
								o1: [EYmEventsO1.ACCOUNT, EYmEventsO1.PUBLISHER, EYmEventsO1.CATALOG],
								o2: EYmEventsO2.NO_ADSPACES_POPUP,
							});
							setOpenedModal(null);
						}}>
						{t('Создать площадку сейчас')}
					</ButtonStyleLink>
				</Flex>
			),
		},
		no_active_source: {
			title: t('Пока вы не можете подать заявку на офферы'),
			text: (
				<>
					<Text type="secondary" style={{ fontSize: 16 }}>
						{t('Чтобы подать заявку на офферы, ваша площадка должна пройти проверку командой Affilead. ')}
					</Text>
					<Text type="secondary" style={{ fontSize: 16 }}>
						{t(
							'Пожалуйста, дождитесь ответа нашей команды. Проверка обычно занимает от {2 часов} до {3 дней}, в зависимости от рабочего времени нашей команды.',
						)}
					</Text>
				</>
			),
			buttons: (
				<Button type="primary" onClick={() => setOpenedModal(null)}>
					{t('Хорошо')}
				</Button>
			),
		},
	};

	return (
		<Modal {...props} width={450} open={!!openedModal} onCancel={() => setOpenedModal(null)} centered footer={null}>
			<Flex vertical gap={24}>
				<Title level={4} style={{ margin: 0 }}>
					{modalMap[openedModal].title}
				</Title>

				<Flex vertical gap={16}>
					{modalMap[openedModal].text}
				</Flex>

				{modalMap[openedModal].buttons}
			</Flex>
		</Modal>
	);
};
